import { action } from "typesafe-actions";
import {
  ICommonState,
  IGeneralHistoryResponse,
  IMail,
  IPolicies,
  IRecordCount,
  IWhatsappCount,
} from "./common.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import {
  closeLoaderWithMessage,
  openLoaderWithMessage,
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { api } from "../../api/api";
import { LoadState } from "../../constants/enums";
import { IFileUpload } from "../../components/FileUpload/FileUpload.type";
import { IDate } from "../../components/Table/hooks/useDateFilter";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import moment from "moment";
import { compareTwoObejcts } from "../../helpers/compareTwoObjects";

const getMonthStartEndDate = (month: string, year: string) => {
  const formattedMonth = month.padStart(2, "0");

  const start = moment(`${year}-${formattedMonth}-01`)
    .startOf("month")
    .format("YYYY-MM-DD");
  const end = moment(`${year}-${formattedMonth}-01`)
    .endOf("month")
    .format("YYYY-MM-DD");

  return { start, end };
};

export const FETCH_POLICIES_PROGRESS = "FETCH_POLICIES_PROGRESS";
export const FETCH_POLICIES_SUCCESS = "FETCH_POLICIES_SUCCESS";
export const FETCH_POLCIES_FAILED = "FETCH_POLCIES_FAILED";

export const fetchPoliciesProgress = () => action(FETCH_POLICIES_PROGRESS);
export const fetchPoliciesSuccess = (policies: IPolicies[]) =>
  action(FETCH_POLICIES_SUCCESS, { policies });
export const fetchPoliciesFailed = () => action(FETCH_POLCIES_FAILED);
export const fetchPolicies =
  (
    company_outlet_uuid?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    const policies = getState().common.policies;
    if (policies.loadState !== LoadState.Loaded) {
      try {
        dispatch(fetchPoliciesProgress());
        let url = `/policy/get-policies`;
        if (company_outlet_uuid) {
          url += `?company_outlet_uuid=${company_outlet_uuid}`;
        }
        const res = await api.get(url);
        const data: IPolicies[] = res.data.data;

        dispatch(fetchPoliciesSuccess(data));
      } catch (err: any) {
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          }),
        );
        dispatch(fetchPoliciesFailed());
      }
    }
  };

export const fetchInitialRefData =
  (): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(fetchPolicies());
  };

export const sendMessageToUserPhone =
  (
    data: {
      mobileNumber: string;
      message: string;
    },
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      await api.post(`/general/send-sms`, {
        recipients: [
          {
            to: "+1" + data.mobileNumber,
            name: data.message,
          },
        ],
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Message send successfully!",
          displayAs: "snackbar",
        }),
      );

      onCallback(true);
    } catch (err: any) {
      onCallback(true);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const sendMessageToWhatsapp =
  (
    data: {
      mobileNumber: string;
      message: string;
    },
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      await api.post(`/general/send-whatsapp`, {
        recipients: [
          {
            to: "+1" + data.mobileNumber,
            name: data.message,
          },
        ],
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Message send successfully!",
          displayAs: "snackbar",
        }),
      );

      onCallback(true);
    } catch (err: any) {
      onCallback(true);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const sendVoiceCalling =
  (
    data: {
      mobileNumber: string;
    },
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      await api.post(`/general/voice-calling`, {
        recipients: [
          {
            to: "+1" + data.mobileNumber,
          },
        ],
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Voice calling is send successfully!",
          displayAs: "snackbar",
        }),
      );

      onCallback(true);
    } catch (err: any) {
      onCallback(true);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const sendEmail =
  (
    data: IMail,
    attachments: IFileUpload[],
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const finalData = { ...data };

      if (attachments.length > 0) {
        const keys: string[] = [];
        for (const attachment of attachments) {
          if (attachment.path) {
            keys.push(attachment.path);
          }
        }
        const bufferJson = await api.post("/general/download-files", {
          type: "json",
          keys,
        });
        finalData.attachments = bufferJson?.data?.map(
          (ele: any, index: number) => ({
            content: ele,
            filename: keys[index].split("/").at(-1),
          }),
        );
      }

      await api.post(`/general/send-grid-email`, {
        ...finalData,
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Email is send successfully!",
          displayAs: "snackbar",
        }),
      );

      onCallback(true);
    } catch (err: any) {
      onCallback(true);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_GENERAL_HISTORY_LIST_PROGRESS =
  "FETCH_GENERAL_HISTORY_LIST_PROGRESS";
export const FETCH_GENERAL_HISTORY_LIST_SUCCESS =
  "FETCH_GENERAL_HISTORY_LIST_SUCCESS";
export const FETCH_GENERAL_HISTORY_LIST_FAILED =
  "FETCH_GENERAL_HISTORY_LIST_FAILED";

export const fetchGeneraHistoryListProgress = () =>
  action(FETCH_GENERAL_HISTORY_LIST_PROGRESS);
export const fetchGeneraHistoryListSuccess = (data: IGeneralHistoryResponse) =>
  action(FETCH_GENERAL_HISTORY_LIST_SUCCESS, { data });
export const fetchGeneraHistoryListFailed = () =>
  action(FETCH_GENERAL_HISTORY_LIST_FAILED);

export const fetchGeneraHistoryListAsync =
  (
    customerPolicyId: number,
    tableName: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchGeneraHistoryListProgress());
      const res = await api.get(
        `/general/get-log-data?table_name=${tableName}&customer_policy_id=${customerPolicyId}`,
      );

      const data: IGeneralHistoryResponse = res.data.data;
      if (data) {
        dispatch(fetchGeneraHistoryListSuccess(data));
      } else {
        dispatch(fetchGeneraHistoryListSuccess({ result: [] }));
      }
    } catch (err: any) {
      dispatch(fetchGeneraHistoryListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const fetchHistoryByTableColumnAsync =
  (
    module_code: string | number,
    company_outlet_uuid?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchGeneraHistoryListProgress());
      let url = `/history/get-history?module_uuid=${module_code}`;
      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }
      const res = await api.get(url);

      const data: IGeneralHistoryResponse = res.data.data;
      if (data) {
        dispatch(fetchGeneraHistoryListSuccess(data));
      } else {
        dispatch(fetchGeneraHistoryListSuccess({ result: [] }));
      }
    } catch (err: any) {
      dispatch(fetchGeneraHistoryListFailed());

      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const CLEAR_GENERAL_HISTORY_STATE = "CLEAR_GENERAL_HISTORY_STATE";

export const clearGeneraHistoryState = () =>
  action(CLEAR_GENERAL_HISTORY_STATE);

export const FETCH_RECORD_COUNTS_PROGRESS = "FETCH_RECORD_COUNTS_PROGRESS";
export const FETCH_RECORD_COUNTS_SUCCESS = "FETCH_RECORD_COUNTS_SUCCESS";
export const FETCH_RECORD_COUNTS_FAILED = "FETCH_RECORD_COUNTS_FAILED";

export const fetchRecordsCountsProgress = () =>
  action(FETCH_RECORD_COUNTS_PROGRESS);
export const fetchRecordsCountsSuccess = (data: IRecordCount[]) =>
  action(FETCH_RECORD_COUNTS_SUCCESS, { data });
export const fetchRecordsCountsFailed = () =>
  action(FETCH_RECORD_COUNTS_FAILED);

interface IFetchRecordCountsPayload {
  tableName: string;
  module_key: string;
  fromDate?: string;
  toDate?: string;
  companyOutlet?: string;
  searchValue?: IDatatableV2AdvancedSearchFilter;
}

export const fetchRecordCountsAsync =
  (
    params: IFetchRecordCountsPayload,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    const {
      searchValue,
      companyOutlet,
      fromDate,
      toDate,
      tableName,
      module_key,
    } = params;
    try {
      dispatch(fetchRecordsCountsProgress());
      let query = "";
      if ((searchValue?.length ?? 0) > 0) {
        query = `&advanceFilter=${encodeURIComponent(JSON.stringify(searchValue))}`;
      }
      // if (searchValue.length > 0) {
      // 	url += `&advanceFilter=${JSON.stringify(searchValue)}`;
      // }

      if (companyOutlet) {
        query += `&company_outlet_uuid=${companyOutlet}`;
      }

      let dateParams = "";
      if (fromDate) {
        dateParams += `&from_date=${fromDate}`;
      }
      if (toDate) {
        dateParams += `&to_date=${toDate}`;
      }

      const res = await api.get(
        `/general/get-record-counts?table_name=${tableName}&module_key=${module_key}&duration_type=ALL_TIMES${dateParams}` +
          query,
      );

      const data: IRecordCount[] = res.data.data;

      dispatch(fetchRecordsCountsSuccess(data || []));
    } catch (err: any) {
      dispatch(fetchRecordsCountsFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const CLEAR_RECORD_COUNTS = "CLEAR_RECORD_COUNTS";
export const clearRecordCounts = () => action(CLEAR_RECORD_COUNTS);

export const FETCH_WHATSAPP_COUNTS_PROGRESS = "FETCH_WHATSAPP_COUNTS_PROGRESS";
export const FETCH_WHATSAPP_COUNTS_SUCCESS = "FETCH_WHATSAPP_COUNTS_SUCCESS";
export const FETCH_WHATSAPP_COUNTS_FAILED = "FETCH_WHATSAPP_COUNTS_FAILED";

export const fetchWhatsappCountsProgress = () =>
  action(FETCH_WHATSAPP_COUNTS_PROGRESS);
export const fetchWhatsappCountsSuccess = (data: IWhatsappCount[]) =>
  action(FETCH_WHATSAPP_COUNTS_SUCCESS, { data });
export const fetchWhatsappCountsFailed = () =>
  action(FETCH_WHATSAPP_COUNTS_FAILED);

export const fetchWhatsappCountsAsync =
  (
    marketCampaignUuid: string,
    tableName: string,
    fromDate?: string,
    toDate?: string,
    companyOutlet?: string,
    searchValue?: IDatatableV2AdvancedSearchFilter,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchWhatsappCountsProgress());
      let query = "";
      if ((searchValue?.length ?? 0) > 0) {
        query = `&advanceFilter=${JSON.stringify(searchValue)}`;
      }

      if (companyOutlet) {
        query += `&company_outlet_uuid=${companyOutlet}`;
      }

      if (marketCampaignUuid) {
        query += `&reference_uuid_column_value=${marketCampaignUuid}`;
      }

      const res = await api.get(
        `/conversation/get-whatsapp-status-count?reference_uuid_column_name=${tableName}` +
          query,
      );

      const data: IWhatsappCount[] = res.data.data;

      dispatch(fetchWhatsappCountsSuccess(data || []));
    } catch (err: any) {
      dispatch(fetchWhatsappCountsFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const CLEAR_WHATSAPP_COUNTS = "CLEAR_WHATSAPP_COUNTS";
export const clearWhatsappCounts = () => action(CLEAR_WHATSAPP_COUNTS);

export const fetchDocumentList =
  (
    data: {
      pdfType: string;
      organizations_uuid?: string;
      courses_uuid?: string;
      searchValue?: IDatatableV2AdvancedSearchFilter;
      date?: IDate;
    },
    onCallback: (isSuccess: boolean, data: any) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(openLoaderWithMessage("Downloading Your FIle, Please wait..."));

      let url = "";

      if (data.organizations_uuid) {
        url += `&organizations_uuid=${data.organizations_uuid}`;
      }
      if (data.courses_uuid) {
        url += `&courses_uuid=${data.courses_uuid}`;
      }
      if (data.searchValue !== undefined && data.searchValue.length > 0) {
        url += `&advanceFilter=${JSON.stringify(data.searchValue)}`;
      }

      if (data.date) {
        url += `&from_date=${data.date.fromDate}&to_date=${data.date.toDate}`;
      }

      let finalUrl = `customer/get-inquiryList-pdf?pdfType=${data.pdfType}${url}`;

      const res = await api.get(finalUrl, {
        responseType: "arraybuffer",
      });

      const blob = new Blob([res.data], { type: "application/pdf" });

      // Create a temporary URL for the Blob
      const get_url = window.URL.createObjectURL(blob);

      // Create a link element and simulate a click to trigger the download
      const link = document.createElement("a");
      link.href = get_url;
      link.setAttribute("download", "FeeReceipt.pdf"); // Set the filename for the downloaded file
      document.body.appendChild(link);
      link.click();

      // Clean up by revoking the URL object
      window.URL.revokeObjectURL(get_url);
      document.body.removeChild(link);
      if (res.status === 200) {
        onCallback(true, res.data.data);
      }
    } catch (error: any) {
      dispatch(
        showMessage({
          type: "error",
          displayAs: "snackbar",
          message: error.message,
        }),
      );
    } finally {
      dispatch(closeLoaderWithMessage());
    }
  };

export const FETCH_MODULES_LIST_PROGRESS = "FETCH_MODULES_LIST_PROGRESS";
export const FETCH_MODULES_LIST_SUCCESS = "FETCH_MODULES_LIST_SUCCESS";
export const FETCH_MODULES_LIST_FAILED = "FETCH_MODULES_LIST_FAILED";

export const fetchModulesListProgress = (isView: boolean) =>
  action(FETCH_MODULES_LIST_PROGRESS, { isView });
export const fetchModulesListSuccess = (data: string[], isView: boolean) =>
  action(FETCH_MODULES_LIST_SUCCESS, {
    data,
    isView,
  });
export const fetchModulesListFailed = (isView: boolean) =>
  action(FETCH_MODULES_LIST_FAILED, {
    isView,
  });

export const fetchModuleAsync =
  (
    tableType: "VIEW" | "BASE TABLE",
    company_outlet_uuid?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    const isView = tableType === "VIEW";
    try {
      const modules = getState().common.modules;
      let isAlreadyFetched = false;
      if (isView) {
        isAlreadyFetched = modules.tableViews.loading === LoadState.Loaded;
      } else {
        isAlreadyFetched = modules.tableNames.loading === LoadState.Loaded;
      }
      if (!isAlreadyFetched) {
        dispatch(fetchModulesListProgress(isView));
        let url =
          "/general/get-table-name-or-column-name?table_type=" + tableType;
        if (company_outlet_uuid) {
          url += `&company_outlet_uuid=${company_outlet_uuid}`;
        }
        const res = await api.get(url);
        dispatch(fetchModulesListSuccess(res.data.data as string[], isView));
      }
    } catch (err: any) {
      dispatch(fetchModulesListFailed(isView));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const fetchColumnsByTableNameAsync =
  (
    tableType: "VIEW" | "BASE TABLE" | "SQL",
    tableName: string,
    onCallback: (columns: any) => void,
    company_outlet_uuid?: string,
    otherParams?: {
      sql_column_name?: string;
      refrence_column_name?: string;
      refrence_column_value?: string;
      is_data_type_required?: boolean;
    },
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      let url =
        "/general/get-table-name-or-column-name?table_type=" +
        tableType +
        "&table_name=" +
        tableName;
      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }
      if (otherParams && otherParams.sql_column_name) {
        url += `&sql_column_name=${otherParams.sql_column_name}`;
      }
      if (otherParams && otherParams.refrence_column_name) {
        url += `&refrence_column_name=${otherParams.refrence_column_name}`;
      }
      if (otherParams && otherParams.refrence_column_value) {
        url += `&refrence_column_value=${otherParams.refrence_column_value}`;
      }
      if (otherParams && otherParams.is_data_type_required) {
        url += `&is_data_type_required=${otherParams.is_data_type_required}`;
      }
      const res = await api.get(url);
      onCallback(res.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const FETCH_PAYMENT_TRANSACTION_STATUS_PROGRESS =
  "FETCH_PAYMENT_TRANSACTION_STATUS_PROGRESS";
export const FETCH_PAYMENT_TRANSACTION_STATUS_SUCCESS =
  "FETCH_PAYMENT_TRANSACTION_STATUS_SUCCESS";
export const FETCH_PAYMENT_TRANSACTION_STATUS_FAILED =
  "FETCH_PAYMENT_TRANSACTION_STATUS_FAILED";

export const fetchPaymentTransactionStatusProgress = () =>
  action(FETCH_PAYMENT_TRANSACTION_STATUS_PROGRESS);
export const fetchPaymentTransactionStatusSuccess = (data: any[]) =>
  action(FETCH_PAYMENT_TRANSACTION_STATUS_SUCCESS, { data });
export const fetchPaymentTransactionStatusFailed = () =>
  action(FETCH_PAYMENT_TRANSACTION_STATUS_FAILED);

export const fetchPaymentTransactionStatusAsync =
  (
    searchValue: IDatatableV2AdvancedSearchFilter,
    searchByNameOrNumber: string,
    searchByVenue: string,
    otherCustomStateProperties: { [key: string]: any },
    companyOutlet?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const {
        searchByEventType,
        functionDate: { month, year },
      } = otherCustomStateProperties;

      dispatch(fetchPaymentTransactionStatusProgress());
      let query = "";

      if (companyOutlet) {
        query += `&company_outlet_uuid=${companyOutlet}`;
      }

      if (
        searchValue.length > 0 ||
        searchByNameOrNumber ||
        searchByVenue ||
        month ||
        year ||
        searchByEventType
      ) {
        const advanceFilter = [...searchValue];

        if (searchByNameOrNumber) {
          advanceFilter.push({
            column: ["name_of_guest", "contact_number_one"],
            value: searchByNameOrNumber,
            logicalOperator: "OR",
            operator: "CONTAINS",
          });
        }

        if (searchByVenue) {
          advanceFilter.push({
            column: ["preferred_venue"],
            value: searchByVenue,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (month && !year) {
          advanceFilter.push({
            column: ["function_date"],
            value: `-${month}-`,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (!month && year) {
          advanceFilter.push({
            column: ["function_date"],
            value: `-${year}-`,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (month && year) {
          const { start, end } = getMonthStartEndDate(month, year);
          advanceFilter.push({
            column: ["function_date"],
            value: start,
            logicalOperator: "AND",
            operator: "GREATER_THAN_EQUAL",
          });
          advanceFilter.push({
            column: ["function_date"],
            value: end,
            logicalOperator: "AND",
            operator: "LESSER_THAN_EQUAL",
          });
        }

        if (searchByEventType && searchByEventType.length > 0) {
          advanceFilter.push({
            column: ["event_type"],
            value: searchByEventType,
            logicalOperator: "AND",
            operator: "IN",
          });
        }
        query += `&advanceFilter=${encodeURIComponent(JSON.stringify(advanceFilter))}`;
      }

      const res = await api.get(
        `/accounting/get-status-payment-transaction?${query}`,
      );

      dispatch(fetchPaymentTransactionStatusSuccess(res.data.data || []));
    } catch (err: any) {
      dispatch(fetchPaymentTransactionStatusFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const FETCH_ENDPOINTS_LIST_PROGRESS = "FETCH_ENDPOINTS_LIST_PROGRESS";
export const FETCH_ENDPOINTS_LIST_SUCCESS = "FETCH_ENDPOINTS_LIST_SUCCESS";
export const FETCH_ENDPOINTS_LIST_FAILED = "FETCH_ENDPOINTS_LIST_FAILED";
export const CLEAR_ENDPOINTS_LIST = "CLEAR_ENDPOINTS_LIST";
export const clearEndpoints = () => action(CLEAR_ENDPOINTS_LIST);

export const fetchEndpointsListProgress = () =>
  action(FETCH_ENDPOINTS_LIST_PROGRESS);
export const fetchEndpointsListSuccess = (
  data: ICommonState["endPointsByModule"]["data"],
) =>
  action(FETCH_ENDPOINTS_LIST_SUCCESS, {
    data,
  });
export const fetchEndpointsListFailed = () =>
  action(FETCH_ENDPOINTS_LIST_FAILED);

export const fetchEndpointsAsync =
  (
    method_type: "post" | "get",
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const { loading } = getState().common.endPointsByModule;
      let isAlreadyFetched = loading === LoadState.Loaded;

      if (!isAlreadyFetched) {
        dispatch(fetchEndpointsListProgress());
        const url = "/workflow/get-apis-endpoints?method_type=" + method_type;
        const res = await api.get(url);
        dispatch(fetchEndpointsListSuccess(res.data.data));
      }
    } catch (err: any) {
      dispatch(fetchEndpointsListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const initiateWorkFlow =
  (
    initialData: any,
    currentData: any,
    apiUrl: string,
    isUpdate: boolean,
    primaryFieldName: string,
    primaryFieldValue: any,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const payload: {
        endpoint_path: string;
        upsert_type: "CREATE" | "UPDATE";
        identifier: {
          column_name: string;
          column_value: any;
        };
        changed_column: {
          [key: string]: {
            old_value: any;
            new_value: any;
          };
        };
        no_changed_column: {
          [key: string]: any;
        };
      } = {
        endpoint_path: "/api/v1" + apiUrl,
        upsert_type: isUpdate ? "UPDATE" : "CREATE",
        identifier: {
          column_name: primaryFieldName,
          column_value: primaryFieldValue || null,
        },
        changed_column: {},
        no_changed_column: {},
      };

      if (isUpdate) {
        const finalData = compareTwoObejcts(initialData, currentData);
        payload.changed_column = finalData.changed;
        payload.no_changed_column = finalData.unchanged;
      } else {
        payload.no_changed_column = currentData;
      }

      await api.post("/workflow/workflow-initiate", payload);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const CLEAR_PAYMENT_TRANSACTION_STATUS =
  "CLEAR_PAYMENT_TRANSACTION_STATUS";
export const clearPaymentTransactionStatus = () =>
  action(CLEAR_PAYMENT_TRANSACTION_STATUS);
